import Route from 'cio/routes/base';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import { inject as service } from '@ember/service';
import { readOnly } from '@ember/object/computed';
import LogModel from 'cio/models/log';
import FacebookLeadAdsForm from 'cio/models/facebook-lead-ads-form';
import AnalyticsService from 'cio/services/analytics';
import FormEventAttributeCondition from 'cio/app-models/conditions/form-event-attribute';
import { queryEncode } from 'cio/helpers/query';
import { serializeFilters } from 'cio/utils/conditions/serialize/form';
import ArrayProxy from '@ember/array/proxy';
import CampaignModel from 'cio/models/campaign';
import deserializeAndNormalizeFormAttributeFilters from 'cio/utils/campaigns/deserialize-normalize-form-attribute-filters';
import { FormTriggeredCampaignFormTriggerFilter } from 'cio/models/campaign';
import Transition from '@ember/routing/-private/transition';
import CustomFormsService from 'cio/services/custom-forms';
import CustomForm from 'cio/models/custom-form';
import {
  FormDetailsURLFormType,
  formDetailsURLFormType,
  formDetailsPageModels,
  isCustomForm,
  SharedFormModelInterface,
} from 'cio/utils/forms';
import EnvironmentService from 'cio/services/environment';
import { all } from 'rsvp';
import RouterService from '@ember/routing/router-service';
import DS from 'ember-data';
import _sortBy from 'lodash.sortby';

function filterEvents(logs: ArrayProxy<LogModel>, matches: string[]) {
  return logs.filter((l: LogModel) => matches.includes(l.id));
}

export default class IntegrationsFormsFormDetails extends Route {
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service customForms!: CustomFormsService;
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;
  @service router!: RouterService;
  @service store!: DS.Store;

  redirect(model: { form: SharedFormModelInterface; urlFormType: FormDetailsURLFormType }, _transition: Transition) {
    let { form, urlFormType } = model;

    // if form type in the URL and actual form type don't match, redirect to correct URL:
    // (it shouldn't happen IRL, as all the links already contain correct form type)
    if (urlFormType !== formDetailsURLFormType(form)) {
      this.router.transitionTo('integrations.forms.form_details', ...formDetailsPageModels(form));
    }

    return;
  }

  async model(params: { id: string; type: FormDetailsURLFormType }) {
    let form: FacebookLeadAdsForm | CustomForm | undefined;

    if (params.type === 'facebook') {
      form = this.facebookLeadAds.forms.findBy('id', params.id);
    } else {
      form = this.customForms.forms.findBy('id', params.id);
    }
    if (!form) {
      throw { errors: [{ status: '404' }] }; // TODO: better error, like actual new Error(...)? Test this more, too
    }

    this.trackManageFormEvent(form);
    let logs = await this.getFormEventLogs(form);
    let campaigns = this.getFormCampaigns(form);

    return {
      form,
      logs,
      campaigns,
      urlFormType: params.type,
    };
  }

  trackManageFormEvent(form: FacebookLeadAdsForm | CustomForm) {
    let { formType, formId } = form;
    this.analytics.track('forms', 'manage_form_started', {
      environmentId: this.environment.current.id,
      action: 'Managing',
      formType,
      formId,
      api: isCustomForm(form) && form.isApiForm,
      scanned: isCustomForm(form) && form.isScannedForm,
    });
  }

  getFormCampaigns(form: FacebookLeadAdsForm | CustomForm) {
    return this.store.peekAll('campaign').filter((c: CampaignModel) => {
      if (!c.isFormTriggered) {
        return false;
      }

      // handle draft form campaigns without selected triggers (no "attributeFilters")
      if (!c.attributeFilters) {
        return false;
      }

      return deserializeAndNormalizeFormAttributeFilters(c.attributeFilters).children.some(
        (c: FormTriggeredCampaignFormTriggerFilter) =>
          c.children[0].value === form.formId.toLowerCase() && c.children[0].formType === form.formType,
      );
    });
  }

  // TODO: investigate against possible pagination bugs when form events aren't on the first page of returned log results (before filtering)
  async getFormEventLogs(form: FacebookLeadAdsForm | CustomForm) {
    let { formType, formId } = form;

    let condition = FormEventAttributeCondition.create({
      name: 'form_id',
      value: formId,
      formType,
    });

    let [failedEvents, events] = await all(
      ['failed_event', 'event'].map(async (type) => {
        let query = {
          type,
          name: 'form_submit',
          since: true,
          filters: queryEncode(serializeFilters([condition], 'and', true)),
        };

        try {
          let result = await this.store.query('log', query);

          return filterEvents(result, (result as any).meta.filter_matches);
        } catch (e) {
          // do nothing: fallback to empty logs array
          return [];
        }
      }),
    );

    return _sortBy([...failedEvents, ...events], ['timestamp']).reverse();
  }

  crumb() {
    let model = this.modelFor(this.routeName) as { form: FacebookLeadAdsForm | CustomForm };
    let nameProperty = model.form instanceof FacebookLeadAdsForm ? 'formNameForDisplay' : 'name';

    return {
      name: readOnly(`model.form.${nameProperty}`),
      path: this.routeName,
      model,
    };
  }
}
